import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { TrendingUp, TrendingDown, Euro, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Financial Pulse — Dashboard widget showing YTD revenue progress
 * against the GPS annual goal. Hides silently for admins without linked
 * player, or if the consultant hasn't configured the goal yet.
 */
export default function FinancialPulseWidget() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/gps/ytd-report").then((r) => setData(r.data)).catch(() => setData({ hidden: true }));
  }, []);

  if (!data) return null;
  if (data.hidden || !data.plan) return null;
  if (!data.plan.is_configured && user?.role === "admin") return null;
  if (!data.plan.is_configured) {
    // Consultant without goal — show CTA
    return (
      <div className="kw-card p-5 flex items-center gap-4" data-testid="financial-pulse-empty">
        <Euro className="w-6 h-6 text-[#22C55E] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="kw-overline">Pulse Financeiro · GPS 1-3-5</div>
          <div className="text-sm text-[#A3A3A3] mt-1">
            Define o teu objetivo anual em € e as tuas conversões no GPS para começares a acompanhar
            o progresso YTD e receberes sugestões automáticas.
          </div>
        </div>
        <Link to="/my-profile#gps" className="kw-btn-secondary text-xs inline-flex items-center gap-2" data-testid="financial-pulse-setup-btn">
          Configurar GPS <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  const eur = (n) => `${Math.round(Number(n) || 0).toLocaleString("pt-PT")} €`;
  const ytd = data.ytd || {};
  const pct = Math.max(0, Math.min(200, ytd.pct || 0));
  const color = pct >= 100 ? "#22C55E" : pct >= 85 ? "#EAB308" : "#E50000";
  const isAhead = (ytd.delta_eur || 0) >= 0;

  return (
    <div className="kw-card p-5" data-testid="financial-pulse-widget">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Euro className="w-4 h-4 text-[#22C55E]" />
          <div>
            <div className="kw-overline">Pulse Financeiro · YTD</div>
            <div className="font-display text-lg font-black uppercase">
              {eur(ytd.actual_eur || 0)} <span className="text-[#737373] text-sm normal-case font-normal">/ {eur(ytd.target_eur || 0)} target</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-[#737373]">YTD</div>
            <div className="font-display text-2xl font-black" style={{ color }}>{pct.toFixed(0)}%</div>
          </div>
          <Link to="/my-profile#ytd-report" className="kw-btn-secondary text-xs inline-flex items-center gap-1" data-testid="financial-pulse-open">
            Relatório <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
      {/* Progress bar */}
      <div className="h-2 bg-[#141414] relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 transition-all"
          style={{ width: `${Math.min(100, pct)}%`, background: color }}
        />
        {/* Overshoot marker */}
        {pct > 100 && (
          <div className="absolute inset-y-0 right-0" style={{ width: `${Math.min(50, pct - 100)}%`, background: "#22C55E", opacity: 0.5 }} />
        )}
      </div>
      <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
        <div className="flex items-center gap-1.5 text-xs">
          {isAhead ? (
            <TrendingUp className="w-3.5 h-3.5 text-[#22C55E]" />
          ) : (
            <TrendingDown className="w-3.5 h-3.5 text-[#E50000]" />
          )}
          <span className={isAhead ? "text-[#22C55E] font-bold" : "text-[#E50000] font-bold"}>
            {isAhead ? "+" : ""}{eur(ytd.delta_eur || 0)}
          </span>
          <span className="text-[#737373]">{isAhead ? "acima do target" : "abaixo do target"}</span>
        </div>
        <div className="text-[10px] text-[#737373] font-mono">
          Plano: {data.plan.contacts_per_week.toFixed(1)} contactos · {data.plan.meetings_per_week.toFixed(1)} reuniões · {data.plan.listings_per_week.toFixed(2)} angariações · {data.plan.sales_per_week.toFixed(2)} vendas / sem
        </div>
      </div>
    </div>
  );
}
